/**
 * VistoeraTASK-00590 / VistoeraTASK-00629 — client-side encode-to-max-bytes
 * for the image editor "按文件大小调整" mode.
 *
 * Prefer quality reduction at the CURRENT pixel size. Do NOT silently
 * downscale (TASK-00629 §五/§六) — if the target is unreachable at the
 * current resolution/format, return `unreachable` / `needs_format_change`
 * so the UI can fail clearly. Never enlarges. Never silently converts a
 * transparent PNG to JPEG.
 */

import { fitWithinMaxBox } from './imageEditorResizeMath'
import { imageDataHasTransparency } from './exportFormatSelection'

export type CompressFailReason =
  | 'already_small_enough'
  | 'needs_format_change'
  | 'unreachable'
  | 'encode_failed'
  | 'cancelled'

export interface CompressSuccess {
  ok: true
  blob: Blob
  width: number
  height: number
  mimeType: string
  bytes: number
  /** True when pixels were scaled (not just re-encoded). */
  dimensionsChanged: boolean
}

export interface CompressFailure {
  ok: false
  reason: CompressFailReason
  message: string
  /** Present when the only path to the target would drop transparency. */
  suggestedOpaqueMime?: 'image/jpeg'
}

export type CompressResult = CompressSuccess | CompressFailure

export interface CompressOptions {
  /** Source canvas already holding the current edited pixels. */
  source: HTMLCanvasElement
  maxBytes: number
  /** Current file's approximate byte size (for "already small enough"). */
  currentBytes?: number
  /** Prefer keeping PNG when transparent; otherwise JPEG. */
  hasTransparency: boolean
  /** Abort when the user changes params / closes the modal. */
  signal?: AbortSignal
  /** Hard cap on encode attempts (quality ladder). */
  maxAttempts?: number
  /**
   * VistoeraTASK-00629 — when true, after quality-at-full-size fails, try
   * aspect-locked downscales. Default false: never silently shrink pixels.
   */
  allowScaleDown?: boolean
}

const JPEG_QUALITIES = [0.92, 0.85, 0.78, 0.7, 0.62, 0.55, 0.48, 0.4, 0.32]
/** Only used when `allowScaleDown` is explicitly true. */
const SCALE_DOWN_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3]

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (quality === undefined) canvas.toBlob((b) => resolve(b), mimeType)
    else canvas.toBlob((b) => resolve(b), mimeType, quality)
  })
}

function scaleCanvas(src: HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const ctx = out.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(src, 0, 0, w, h)
  return out
}

function aborted(signal?: AbortSignal): boolean {
  return Boolean(signal?.aborted)
}

/**
 * Encode `source` so the resulting blob is ≤ `maxBytes`. Returns a
 * failure with `needs_format_change` when a transparent source cannot
 * reach the target as PNG and would require opaque JPEG (caller must
 * confirm with the user before retrying with `hasTransparency: false`).
 */
export async function compressCanvasToMaxBytes(opts: CompressOptions): Promise<CompressResult> {
  const { source, maxBytes, hasTransparency, signal } = opts
  const maxAttempts = opts.maxAttempts ?? 48
  const allowScaleDown = Boolean(opts.allowScaleDown)
  if (!Number.isFinite(maxBytes) || maxBytes <= 0) {
    return { ok: false, reason: 'encode_failed', message: 'invalid max bytes' }
  }
  if (opts.currentBytes != null && opts.currentBytes > 0 && opts.currentBytes <= maxBytes) {
    return {
      ok: false,
      reason: 'already_small_enough',
      message: 'current file already meets the target size',
    }
  }

  const srcW = source.width
  const srcH = source.height
  if (srcW < 1 || srcH < 1) {
    return { ok: false, reason: 'encode_failed', message: 'empty canvas' }
  }

  // Detect transparency from pixels when the caller isn't sure.
  let opaquePreferred = !hasTransparency
  if (!opaquePreferred) {
    try {
      const sample = source.getContext('2d')!.getImageData(0, 0, Math.min(srcW, 64), Math.min(srcH, 64))
      opaquePreferred = !imageDataHasTransparency(sample.data)
    } catch {
      /* keep hasTransparency flag */
    }
  }

  const mimePrimary: 'image/png' | 'image/jpeg' = opaquePreferred ? 'image/jpeg' : 'image/png'
  let attempts = 0
  let best: { blob: Blob; w: number; h: number; mime: string } | null = null
  const scaleSteps = allowScaleDown ? [1, ...SCALE_DOWN_STEPS] : [1]

  for (const scale of scaleSteps) {
    if (aborted(signal)) return { ok: false, reason: 'cancelled', message: 'cancelled' }
    const targetW = Math.max(1, Math.round(srcW * scale))
    const targetH = Math.max(1, Math.round(srcH * scale))
    // Keep aspect via the shared fit helper (defensive against rounding).
    const fitted = fitWithinMaxBox(srcW, srcH, targetW, targetH)
    if (!fitted.ok) continue
    const canvas = fitted.width === srcW && fitted.height === srcH ? source : scaleCanvas(source, fitted.width, fitted.height)

    if (mimePrimary === 'image/png') {
      attempts += 1
      if (attempts > maxAttempts) break
      const blob = await canvasToBlob(canvas, 'image/png')
      if (aborted(signal)) return { ok: false, reason: 'cancelled', message: 'cancelled' }
      if (!blob) continue
      if (!best || blob.size < best.blob.size) best = { blob, w: fitted.width, h: fitted.height, mime: 'image/png' }
      if (blob.size <= maxBytes) {
        return {
          ok: true,
          blob,
          width: fitted.width,
          height: fitted.height,
          mimeType: 'image/png',
          bytes: blob.size,
          dimensionsChanged: fitted.width !== srcW || fitted.height !== srcH,
        }
      }
      // PNG has no quality knob — only continue if scale-down is allowed.
      continue
    }

    for (const q of JPEG_QUALITIES) {
      attempts += 1
      if (attempts > maxAttempts) break
      if (aborted(signal)) return { ok: false, reason: 'cancelled', message: 'cancelled' }
      const blob = await canvasToBlob(canvas, 'image/jpeg', q)
      if (!blob) continue
      if (!best || blob.size < best.blob.size) best = { blob, w: fitted.width, h: fitted.height, mime: 'image/jpeg' }
      if (blob.size <= maxBytes) {
        return {
          ok: true,
          blob,
          width: fitted.width,
          height: fitted.height,
          mimeType: 'image/jpeg',
          bytes: blob.size,
          dimensionsChanged: fitted.width !== srcW || fitted.height !== srcH,
        }
      }
    }
    if (attempts > maxAttempts) break
  }

  if (!opaquePreferred && best && best.blob.size > maxBytes) {
    return {
      ok: false,
      reason: 'needs_format_change',
      message: 'cannot reach target size while preserving transparency',
      suggestedOpaqueMime: 'image/jpeg',
    }
  }

  return {
    ok: false,
    reason: 'unreachable',
    message: 'unable to reach target size within quality and scale limits',
  }
}

/** Probe whether a canvas has any non-opaque pixel (full scan, for confirm). */
export function canvasHasTransparency(canvas: HTMLCanvasElement): boolean {
  try {
    const data = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height).data
    return imageDataHasTransparency(data)
  } catch {
    return false
  }
}
